/**
 * ClientsDetailController — page `clients-detail.html?id={id}` (EPIC-009 / 9.5).
 *
 * Hydrate la fiche client depuis `GET /clients/{id}` (avec
 * `paymentScore` calculé par 9.3 `ClientPaymentScoreService::compute`).
 * Affiche :
 *   - en-tête (raison sociale, type, statut, méta « Client depuis »),
 *   - 4 indicateurs de score paiement (délai moyen, % retard,
 *     exposition CA, badge mauvais payeur),
 *   - mention « données insuffisantes » si tous les indicateurs sont
 *     nuls (cf. §3 specs : `null` partout tant qu'aucune facture payée
 *     sur 12 mois — état attendu tant qu'EPIC-013 n'est pas livré),
 *   - cards Coordonnées + Contacts (clonées depuis `<template>`),
 *   - bouton « Archiver » / « Restaurer » avec `<dialog>` de confirm.
 *
 * Convention CLAUDE.md respectée : pas d'HTML inline en JS (clone de
 * `<template>` + remplissage data-slot + setAttribute). Aucune classe
 * inventée — toutes les classes utilisées viennent de notamicro.css
 * (copie bit-à-bit de maquettes/shared/styles.css).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";
import { buildEventMapping } from "/js/utils/activity_feed_mapping.js";

// Mappings de statut repris verbatim des listes factures/devis
// (invoices_list_controller / quotes_list_controller).
const INVOICE_STATUS_LABELS = {
  draft: "brouillon",
  scheduled: "programmée",
  issued: "émise",
  sent: "envoyée",
  paid: "payée",
  partially_paid: "partiellement payée",
  overdue: "en retard",
  cancelled: "annulée",
  archived: "archivée",
};

const INVOICE_STATUS_PILL_MOD = {
  draft: "nm-pill--indigo",
  scheduled: "nm-pill--scheduled",
  issued: "nm-pill--ochre",
  sent: "nm-pill--ochre",
  paid: "nm-pill--olive",
  partially_paid: "nm-pill--blue",
  overdue: "nm-pill--red",
  cancelled: "",
  archived: "",
};

const QUOTE_STATUS_LABELS = {
  draft: "brouillon",
  issued: "émis",
  sent: "envoyé",
  accepted: "accepté",
  refused: "refusé",
  partially_invoiced: "partiellement facturé",
  fully_invoiced: "facturé",
  expired: "expiré",
  archived: "archivé",
};

const QUOTE_STATUS_PILL_MOD = {
  draft: "nm-pill--indigo",
  issued: "nm-pill--ochre",
  sent: "nm-pill--ochre",
  accepted: "nm-pill--olive",
  refused: "nm-pill--red",
  partially_invoiced: "nm-pill--blue",
  fully_invoiced: "nm-pill--blue",
  expired: "",
  archived: "",
};

const TYPE_LABELS = {
  b2b: "Client · entreprise",
  b2c: "Client · particulier",
  public_administration: "Client · administration",
};

const STATUS_LABELS = {
  active: "actif",
  archived: "archivé",
};

const STATUS_PILL_MOD = {
  active: "nm-pill--olive",
  archived: "nm-pill--ochre",
};

const MONTH_LABELS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

export default class ClientsDetailController extends BaseController {
  static targets = [
    "crumb",
    "title",
    "typeLabel",
    "statusPill",
    "statusLabel",
    "meta",
    "editButton",
    "loadError",
    "statsSection",
    "scoreEmpty",
    "avgDelay",
    "avgDelayHint",
    "latePct",
    "latePctHint",
    "exposure",
    "scorePill",
    "scoreLabel",
    "scoreHint",
    "coordName",
    "coordLines",
    "contactsLines",
    "archiveDescription",
    "invoicesBody",
    "quotesBody",
    "historyList",
    "archiveButton",
    "restoreButton",
    "archiveModal",
    "archiveError",
    "archiveConfirm",
    "restoreModal",
    "restoreError",
    "restoreConfirm",
  ];

  connect() {
    this._clientId = this._parseClientIdFromPath();
    if (!this._clientId) {
      this._renderLoadError("Identifiant client manquant dans l'URL.");
      return;
    }
    this.refresh();
  }

  // ---------------------------------------------------------------------------
  // Path parsing — `clients-detail.html?id={id}` (EPIC-099.53).
  // ---------------------------------------------------------------------------

  _parseClientIdFromPath() {
    // Forme canonique (EPIC-099.53) : clients-detail.html?id={uuid}
    const params = new URLSearchParams(window.location.search);
    return params.get("id");
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async refresh() {
    try {
      const client = await api.getClient(this._clientId);
      this._client = client;
      this._render(client);
      // Sections de consultation (99.57) — 3 fetch indépendants : l'échec
      // de l'un n'empêche pas le rendu du reste de la fiche.
      this._loadInvoices();
      this._loadQuotes();
      this._loadHistory();
    } catch (error) {
      const msg = error instanceof APIError && error.status === 404
        ? "Client introuvable. Il a peut-être été supprimé ou vous n'avez pas accès à cette organisation."
        : (error && error.message) || "Impossible de charger la fiche client.";
      this._renderLoadError(msg);
    }
  }

  _render(client) {
    if (this.hasLoadErrorTarget) this.loadErrorTarget.hidden = true;

    const legalName = client.legalName || "—";
    if (this.hasTitleTarget) this.titleTarget.textContent = legalName;
    if (this.hasCrumbTarget) this.crumbTarget.textContent = legalName;
    document.title = `Notamicro — ${legalName}`;

    if (this.hasTypeLabelTarget) {
      this.typeLabelTarget.textContent = TYPE_LABELS[client.type] ?? "Client";
    }

    this._renderStatusPill(client.status);
    this._renderMeta(client);
    this._renderEditButton();
    this._renderScore(client.paymentScore);
    this._renderCoord(client);
    this._renderContacts(client.contacts);
    this._renderArchiveButtons(client.status);
  }

  _renderStatusPill(status) {
    if (!this.hasStatusPillTarget) return;
    const pill = this.statusPillTarget;
    pill.hidden = false;
    // Retirer les modifiers connus avant d'appliquer le bon.
    for (const m of Object.values(STATUS_PILL_MOD)) pill.classList.remove(m);
    pill.classList.add(STATUS_PILL_MOD[status] ?? "nm-pill--olive");
    if (this.hasStatusLabelTarget) {
      this.statusLabelTarget.textContent = STATUS_LABELS[status] ?? status ?? "—";
    }
  }

  _renderMeta(client) {
    if (!this.hasMetaTarget) return;
    const created = client.createdAt ? new Date(client.createdAt) : null;
    if (!created || Number.isNaN(created.getTime())) {
      this.metaTarget.textContent = "";
      return;
    }
    const label = `Client depuis ${MONTH_LABELS[created.getMonth()]} ${created.getFullYear()}`;
    this.metaTarget.textContent = label;
  }

  _renderEditButton() {
    if (!this.hasEditButtonTarget) return;
    this.editButtonTarget.setAttribute(
      "href",
      `/clients-edition.html?id=${encodeURIComponent(this._clientId)}`,
    );
  }

  // ---------------------------------------------------------------------------
  // Score paiement
  // ---------------------------------------------------------------------------

  _renderScore(score) {
    const safe = score && typeof score === "object" ? score : {};
    const avg = this._asNumber(safe.averagePaymentDelayDays);
    const latePct = this._asNumber(safe.lateInvoicePercentage);
    const exposure = this._asNumber(safe.revenueExposurePercentage);
    const badPayer = safe.badPayerBadge === true;

    const hasAnyData = avg !== null || latePct !== null || exposure !== null;

    if (this.hasStatsSectionTarget) this.statsSectionTarget.hidden = !hasAnyData;
    if (this.hasScoreEmptyTarget) this.scoreEmptyTarget.hidden = hasAnyData;
    if (!hasAnyData) return;

    if (this.hasAvgDelayTarget) {
      this.avgDelayTarget.textContent = "";
      if (avg !== null) {
        const value = document.createTextNode(this._formatNumber(avg, 0));
        const unit = document.createElement("small");
        unit.textContent = " jours";
        this.avgDelayTarget.appendChild(value);
        this.avgDelayTarget.appendChild(unit);
      } else {
        this.avgDelayTarget.textContent = "—";
      }
    }
    if (this.hasAvgDelayHintTarget) {
      this.avgDelayHintTarget.textContent = avg !== null
        ? "Sur les factures payées · 12 mois glissants"
        : "Pas encore de facture payée";
    }

    if (this.hasLatePctTarget) {
      this.latePctTarget.textContent = "";
      if (latePct !== null) {
        const value = document.createTextNode(this._formatNumber(latePct, 0));
        const unit = document.createElement("small");
        unit.textContent = " %";
        this.latePctTarget.appendChild(value);
        this.latePctTarget.appendChild(unit);
      } else {
        this.latePctTarget.textContent = "—";
      }
    }
    if (this.hasLatePctHintTarget) {
      this.latePctHintTarget.textContent = latePct !== null
        ? "Factures payées avec retard · 12 mois glissants"
        : "Pas encore de facture payée";
    }

    if (this.hasExposureTarget) {
      this.exposureTarget.textContent = "";
      if (exposure !== null) {
        const value = document.createTextNode(this._formatNumber(exposure, 0));
        const unit = document.createElement("small");
        unit.textContent = " %";
        this.exposureTarget.appendChild(value);
        this.exposureTarget.appendChild(unit);
      } else {
        this.exposureTarget.textContent = "—";
      }
    }

    this._renderScorePill(badPayer, hasAnyData);
  }

  _renderScorePill(isBadPayer, hasAnyData) {
    if (!this.hasScorePillTarget || !this.hasScoreLabelTarget) return;
    const pill = this.scorePillTarget;
    for (const m of ["nm-pill--olive", "nm-pill--red", "nm-pill--ochre"]) {
      pill.classList.remove(m);
    }
    if (!hasAnyData) {
      pill.classList.add("nm-pill--ochre");
      this.scoreLabelTarget.textContent = "indéterminé";
      if (this.hasScoreHintTarget) {
        this.scoreHintTarget.textContent = "Données insuffisantes (aucune facture payée)";
      }
      return;
    }
    if (isBadPayer) {
      pill.classList.add("nm-pill--red");
      this.scoreLabelTarget.textContent = "mauvais payeur";
      if (this.hasScoreHintTarget) {
        this.scoreHintTarget.textContent = "Au moins un seuil organisation dépassé";
      }
    } else {
      pill.classList.add("nm-pill--olive");
      this.scoreLabelTarget.textContent = "fiable";
      if (this.hasScoreHintTarget) {
        this.scoreHintTarget.textContent = "Sous les seuils définis dans les paramètres";
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Coordonnées / Contacts
  // ---------------------------------------------------------------------------

  _renderCoord(client) {
    if (this.hasCoordNameTarget) {
      this.coordNameTarget.textContent = client.legalName || "—";
    }
    if (!this.hasCoordLinesTarget) return;
    const addresses = Array.isArray(client.addresses) ? client.addresses : [];
    const billing = addresses.find((a) => a.type === "billing" && a.isDefault)
      ?? addresses.find((a) => a.type === "billing")
      ?? null;

    while (this.coordLinesTarget.firstChild) {
      this.coordLinesTarget.removeChild(this.coordLinesTarget.firstChild);
    }

    if (!billing && !client.siret && !client.siren && !client.vatIntracom) {
      const span = document.createElement("span");
      span.className = "is-muted";
      span.textContent = "Aucune adresse renseignée.";
      this.coordLinesTarget.appendChild(span);
      return;
    }

    const fragment = this.cloneTemplate("tpl-coord-lines");
    const cityLine = billing
      ? `${billing.postalCode || ""} ${billing.city || ""}${billing.country && billing.country !== "FR" ? `, ${billing.country}` : ", France"}`.trim()
      : "";

    this._setText(fragment, "line1", billing?.line1, ["line1", "line1Br"]);
    this._setText(fragment, "line2", billing?.line2, ["line2", "line2Br"]);

    if (cityLine) {
      this.fillSlots(fragment, { cityLine });
      const br = fragment.querySelector('[data-slot-element="cityBr"]');
      if (br) br.hidden = false;
    } else {
      const cityEl = fragment.querySelector('[data-slot="cityLine"]');
      if (cityEl) cityEl.remove();
    }

    const siretValue = client.siret || client.siren;
    this._setText(
      fragment,
      "siretLine",
      siretValue ? `${client.siret ? "SIRET" : "SIREN"} ${siretValue}` : null,
      ["siretLine", "siretBr"],
    );
    this._setText(
      fragment,
      "vatLine",
      client.vatIntracom ? `TVA ${client.vatIntracom}` : null,
      ["vatLine"],
    );

    this.coordLinesTarget.appendChild(fragment);
  }

  _setText(fragment, dataSlotName, value, elementKeys = []) {
    const el = fragment.querySelector(`[data-slot-element="${dataSlotName}"]`);
    if (!el) return;
    if (value === null || value === undefined || value === "") {
      // Cacher l'élément + son séparateur éventuel
      for (const k of elementKeys) {
        const kEl = fragment.querySelector(`[data-slot-element="${k}"]`);
        if (kEl) kEl.remove();
      }
      return;
    }
    el.textContent = value;
    el.hidden = false;
    for (const k of elementKeys) {
      const kEl = fragment.querySelector(`[data-slot-element="${k}"]`);
      if (kEl) kEl.hidden = false;
    }
  }

  _renderContacts(rawContacts) {
    if (!this.hasContactsLinesTarget) return;
    const contacts = Array.isArray(rawContacts)
      ? rawContacts.filter((c) => c.status !== "archived")
      : [];

    while (this.contactsLinesTarget.firstChild) {
      this.contactsLinesTarget.removeChild(this.contactsLinesTarget.firstChild);
    }

    if (contacts.length === 0) {
      const span = document.createElement("span");
      span.className = "is-muted";
      span.textContent = "Aucun contact renseigné.";
      this.contactsLinesTarget.appendChild(span);
      return;
    }

    // Primaire d'abord, puis additionnels.
    contacts.sort((a, b) => Number(b.isPrimary === true) - Number(a.isPrimary === true));

    contacts.forEach((c, index) => {
      const fragment = this.cloneTemplate("tpl-contact-line");

      const fullName = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email;
      this.fillSlots(fragment, {
        name: fullName,
        email: c.email,
      });

      const emailLink = fragment.querySelector('[data-slot-attr-href="mailto"]');
      if (emailLink && c.email) emailLink.setAttribute("href", `mailto:${c.email}`);

      const role = c.function;
      const roleSpan = fragment.querySelector('[data-slot-element="roleSpan"]');
      if (role && roleSpan) {
        roleSpan.hidden = false;
        const roleText = roleSpan.querySelector('[data-slot="role"]');
        if (roleText) roleText.textContent = role;
      } else if (roleSpan) {
        roleSpan.remove();
      }

      const phoneSpan = fragment.querySelector('[data-slot-element="phoneSpan"]');
      if (c.phone && phoneSpan) {
        phoneSpan.hidden = false;
        const phoneText = phoneSpan.querySelector('[data-slot="phone"]');
        if (phoneText) phoneText.textContent = c.phone;
      } else if (phoneSpan) {
        phoneSpan.remove();
      }

      this.contactsLinesTarget.appendChild(fragment);
      if (index < contacts.length - 1) {
        // Espacement entre deux contacts (deux <br/> visuel équivalent au maquette).
        this.contactsLinesTarget.appendChild(document.createElement("br"));
        this.contactsLinesTarget.appendChild(document.createElement("br"));
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Sections de consultation — Factures / Devis / Historique (99.57)
  // ---------------------------------------------------------------------------

  async _loadInvoices() {
    if (!this.hasInvoicesBodyTarget) return;
    try {
      const response = await api.listInvoices({ clientId: this._clientId, perPage: 5 });
      this._renderInvoices(Array.isArray(response?.data) ? response.data : []);
    } catch {
      this._renderTableMessage(this.invoicesBodyTarget, "Impossible de charger les factures.");
    }
  }

  _renderInvoices(rows) {
    const body = this.invoicesBodyTarget;
    while (body.firstChild) body.removeChild(body.firstChild);
    if (rows.length === 0) {
      body.appendChild(this.cloneTemplate("tpl-invoice-empty"));
      return;
    }
    for (const inv of rows) {
      const fragment = this.cloneTemplate("tpl-invoice-row");
      this.fillSlots(fragment, {
        ref: inv.number ?? "(brouillon)",
        date: inv.dateEmission ? this.formatDate(inv.dateEmission) : "—",
        amount: this._formatAmount(inv.amountInclVat),
        status: INVOICE_STATUS_LABELS[inv.status] ?? inv.status ?? "—",
      });
      const link = fragment.querySelector('[data-slot-attr-href="detailUrl"]');
      if (link) link.setAttribute("href", `/facture-detail.html?id=${encodeURIComponent(inv.id)}`);
      const dateEl = fragment.querySelector('[data-slot-attr-datetime="dateIso"]');
      if (dateEl && inv.dateEmission) dateEl.setAttribute("datetime", inv.dateEmission);
      const pill = fragment.querySelector('[data-slot-element="statusPill"]');
      const mod = INVOICE_STATUS_PILL_MOD[inv.status];
      if (pill && mod) pill.classList.add(mod);
      body.appendChild(fragment);
    }
  }

  async _loadQuotes() {
    if (!this.hasQuotesBodyTarget) return;
    try {
      const response = await api.listQuotes({ clientId: this._clientId, perPage: 5 });
      this._renderQuotes(Array.isArray(response?.data) ? response.data : []);
    } catch {
      this._renderTableMessage(this.quotesBodyTarget, "Impossible de charger les devis.");
    }
  }

  _renderQuotes(rows) {
    const body = this.quotesBodyTarget;
    while (body.firstChild) body.removeChild(body.firstChild);
    if (rows.length === 0) {
      body.appendChild(this.cloneTemplate("tpl-quote-empty"));
      return;
    }
    for (const quote of rows) {
      const fragment = this.cloneTemplate("tpl-quote-row");
      this.fillSlots(fragment, {
        ref: quote.number ?? "(brouillon)",
        date: quote.dateCreated ? this.formatDate(quote.dateCreated) : "—",
        totalHt: this._formatAmount(quote.amountExclVat),
        status: QUOTE_STATUS_LABELS[quote.status] ?? quote.status ?? "—",
      });
      const link = fragment.querySelector('[data-slot-attr-href="detailUrl"]');
      if (link) link.setAttribute("href", `/devis-detail.html?id=${encodeURIComponent(quote.id)}`);
      const dateEl = fragment.querySelector('[data-slot-attr-datetime="dateIso"]');
      if (dateEl && quote.dateCreated) dateEl.setAttribute("datetime", quote.dateCreated);
      const pill = fragment.querySelector('[data-slot-element="statusPill"]');
      const mod = QUOTE_STATUS_PILL_MOD[quote.status];
      if (pill && mod) pill.classList.add(mod);
      body.appendChild(fragment);
    }
  }

  async _loadHistory() {
    if (!this.hasHistoryListTarget) return;
    try {
      const response = await api.activityFeed.list({
        "filter[clientId]": this._clientId,
        perPage: 10,
        sort: "-occurredAt",
      });
      this._renderHistory(Array.isArray(response?.data) ? response.data : []);
    } catch {
      this._renderHistoryMessage("Impossible de charger l'historique.");
    }
  }

  _renderHistory(items) {
    const list = this.historyListTarget;
    while (list.firstChild) list.removeChild(list.firstChild);
    if (items.length === 0) {
      list.appendChild(this.cloneTemplate("tpl-history-empty"));
      return;
    }
    for (const event of items) {
      const fragment = this.cloneTemplate("tpl-history-item");
      const mapping = buildEventMapping(event);
      const occurredAt = event?.occurredAt ?? "";
      this.fillSlots(fragment, {
        when: this.formatTimeAgo(occurredAt),
        what: mapping.label,
        who: mapping.domainLabel,
      });
      const whenEl = fragment.querySelector('[data-slot="when"]');
      if (whenEl && occurredAt) whenEl.setAttribute("title", occurredAt);
      list.appendChild(fragment);
    }
  }

  _renderTableMessage(body, message) {
    while (body.firstChild) body.removeChild(body.firstChild);
    const tr = document.createElement("tr");
    const td = document.createElement("td");
    td.colSpan = 4;
    td.className = "is-muted";
    td.style.cssText = "padding: 20px; text-align: center;";
    td.textContent = message;
    tr.appendChild(td);
    body.appendChild(tr);
  }

  _renderHistoryMessage(message) {
    const list = this.historyListTarget;
    while (list.firstChild) list.removeChild(list.firstChild);
    const li = document.createElement("li");
    li.className = "is-muted fs-13";
    li.textContent = message;
    list.appendChild(li);
  }

  // ---------------------------------------------------------------------------
  // Archive / restore
  // ---------------------------------------------------------------------------

  _renderArchiveButtons(status) {
    const isArchived = status === "archived";
    if (this.hasArchiveButtonTarget) this.archiveButtonTarget.hidden = isArchived;
    if (this.hasRestoreButtonTarget) this.restoreButtonTarget.hidden = !isArchived;
    if (this.hasArchiveDescriptionTarget) {
      this.archiveDescriptionTarget.textContent = isArchived
        ? "Ce client est archivé : masqué des sélecteurs et listes par défaut. La restauration le réactive immédiatement."
        : "L'archivage masque le client des sélecteurs et listes par défaut, mais conserve l'historique de facturation à des fins légales.";
    }
  }

  openArchiveDialog() {
    if (!this.hasArchiveModalTarget) return;
    if (this.hasArchiveErrorTarget) this.archiveErrorTarget.hidden = true;
    if (this.hasArchiveConfirmTarget) this.archiveConfirmTarget.disabled = false;
    this.archiveModalTarget.showModal();
  }

  closeArchiveDialog() {
    if (this.hasArchiveModalTarget) this.archiveModalTarget.close();
  }

  async archiveConfirm() {
    if (this.hasArchiveConfirmTarget) this.archiveConfirmTarget.disabled = true;
    try {
      await api.archiveClient(this._clientId);
      this.closeArchiveDialog();
      this.showSuccess("Client archivé.");
      this.refresh();
    } catch (error) {
      const msg = (error && error.message) || "Erreur lors de l'archivage.";
      if (this.hasArchiveErrorTarget) {
        this.archiveErrorTarget.textContent = msg;
        this.archiveErrorTarget.hidden = false;
      }
      if (this.hasArchiveConfirmTarget) this.archiveConfirmTarget.disabled = false;
    }
  }

  openRestoreDialog() {
    if (!this.hasRestoreModalTarget) return;
    if (this.hasRestoreErrorTarget) this.restoreErrorTarget.hidden = true;
    if (this.hasRestoreConfirmTarget) this.restoreConfirmTarget.disabled = false;
    this.restoreModalTarget.showModal();
  }

  closeRestoreDialog() {
    if (this.hasRestoreModalTarget) this.restoreModalTarget.close();
  }

  async restoreConfirm() {
    if (this.hasRestoreConfirmTarget) this.restoreConfirmTarget.disabled = true;
    try {
      await api.restoreClient(this._clientId);
      this.closeRestoreDialog();
      this.showSuccess("Client restauré.");
      this.refresh();
    } catch (error) {
      const msg = (error && error.message) || "Erreur lors de la restauration.";
      if (this.hasRestoreErrorTarget) {
        this.restoreErrorTarget.textContent = msg;
        this.restoreErrorTarget.hidden = false;
      }
      if (this.hasRestoreConfirmTarget) this.restoreConfirmTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _renderLoadError(message) {
    if (this.hasLoadErrorTarget) {
      this.loadErrorTarget.textContent = message;
      this.loadErrorTarget.hidden = false;
    }
    if (this.hasMetaTarget) this.metaTarget.textContent = "";
    if (this.hasEditButtonTarget) this.editButtonTarget.hidden = true;
  }

  _asNumber(value) {
    if (value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  _formatNumber(value, digits = 0) {
    return new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  }

  _formatAmount(raw) {
    if (raw === null || raw === undefined || raw === "") return "—";
    const num = Number(raw);
    if (!Number.isFinite(num)) return raw;
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
